import React, { useCallback, useEffect, useRef, useState } from "react";
import Dropdown from "../../components/input/Dropdown";
import {
  approveReport,
  cancelReport,
  downloadFile,
  getReportDownloadInfo,
  getReportNames,
  searchReportQueue,
} from "../../api/reportGeneratorApi";
import Input from "../../components/input/Input";
import Button from "../../components/input/Button";
import Grid from "../../components/Grid";
import LoadingSpinner from "../../components/LoadingSpinner";
import { REPORT_STATUSES } from "./reportStatus";
import { toast } from "react-toastify";
import { ResizableScrollingTextRenderer } from "../../components/renderer/StyleRenderer";
import { formatToDateOnlyFromJsonDate } from "../../dateConfig";
import { ReportStatusCellRenderer } from "./ReportStatusCellRenderer";
import { errorMessageToast } from "../../helper";
import { useParams, useNavigate, useLocation } from "react-router-dom";

const ReportQueue = () => {
  const [reportTypes, setReportTypes] = useState([]);
  const [selectedReport, setSelectedReport] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchClicked, setSearchClicked] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [currentSearchRequest, setCurrentSearchRequest] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isAutoSearchMode, setIsAutoSearchMode] = useState(false);

  const params = useParams();
  const navigate = useNavigate();
  const gridRef = useRef();
  const hasAutoSearched = useRef(false);
  const previousParamId = useRef(null);
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const response = await getReportNames();
        setReportTypes(response);
      } catch (error) {
        console.error("Failed to load report types:", error);
        errorMessageToast(error);
      }
    })();
  }, []);

  useEffect(() => {
    const handleAutoSearch = async () => {
      if (
        params?.id &&
        reportTypes.length > 0 &&
        params.id !== previousParamId.current
      ) {
        previousParamId.current = params.id;
        hasAutoSearched.current = true;
        setIsAutoSearchMode(true);

        setSelectedReport("");
        setFromDate("");
        setToDate("");

        const payload = {
          reportId: null,
          fromDate: null,
          toDate: null,
          status: null,
          requestId: params.id,
        };

        setCurrentSearchRequest(payload);
        await searchHandler(payload);
      }
    };

    handleAutoSearch();
  }, [params?.id, reportTypes]);
  const selectFirstRowInGrid = () => {
    const gridApi = gridRef.current?.getGridApi?.();
    if (!gridApi) return;

    gridApi.deselectAll();
    const firstNode = gridApi.getDisplayedRowAtIndex(0);
    if (firstNode) {
      firstNode.setSelected(true);
    }
  };

  useEffect(() => {
    if (isAutoSearchMode && results.length === 1) {
      setTimeout(() => {
        selectFirstRowInGrid();
      }, 0);
    }
  }, [results, isAutoSearchMode]);

  const searchHandler = async (payload) => {
    try {
      setIsSearchLoading(true);
      setSearchClicked(true);
      setSelectedRows([]);

      const response = await searchReportQueue(payload);

      if (response && response.reportData && response.reportData.length > 0) {
        const formattedData = response.reportData.map((report) => ({
          ...report,
          requestDateFormatted: report.requestDate
            ? formatToDateOnlyFromJsonDate(report.requestDate)
            : "",
          startedAtFormatted: report.startedAt
            ? formatToDateOnlyFromJsonDate(report.startedAt)
            : "",
          completedAtFormatted: report.completedAt
            ? formatToDateOnlyFromJsonDate(report.completedAt)
            : "",
        }));

        setResults(formattedData);

        if (payload.requestId) {
          toast.success(`Found report: ${formattedData[0].reportAlias}`, {
            autoClose: 3000,
            containerId: "Main",
          });
        }
      } else {
        setResults([]);

        if (payload.requestId) {
          toast.warning("Report not found or may have been removed.", {
            autoClose: 4000,
            containerId: "Main",
          });
          navigate("/mis/report-generator/queue", { replace: true });
        } else {
          toast.info("No reports found matching your criteria.", {
            autoClose: 3000,
            containerId: "Main",
          });
        }
      }
    } catch (error) {
      console.error("Search error:", error);
      errorMessageToast(error);
      setResults([]);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handleSearch = async () => {
    setIsAutoSearchMode(false);
    hasAutoSearched.current = false;
    previousParamId.current = null;

    if (params?.id) {
      navigate("/mis/report-generator/queue", { replace: true });
    }

    const selectedReportObj = reportTypes.find(
      (r) => r.value === selectedReport,
    );

    const payload = {
      reportId: selectedReportObj?.key ?? null,
      fromDate: fromDate || null,
      toDate: toDate || null,
      status: null,
      requestId: null,
    };

    setCurrentSearchRequest(payload);
    await searchHandler(payload);
  };

  const handleSelectionChanged = useCallback(() => {
    const selectedData = gridRef.current?.getSelectedData();
    setSelectedRows(selectedData || []);
  }, []);

  const actionMenuHandler = async (data) => {
    if (selectedRows.length === 0) {
      toast.warn("Please select a report", {
        autoClose: 3000,
        containerId: "Main",
      });
      return;
    }

    if (selectedRows.length > 1) {
      toast.warn("Please select only one report", {
        autoClose: 3000,
        containerId: "Main",
      });
      return;
    }

    setIsSearchLoading(true);

    try {
      const actionType = data.value;
      const selectedRow = selectedRows[0];

      switch (actionType) {
        case "APPROVE":
          await handleApprove(selectedRow);
          break;
        case "CANCEL":
          await handleCancel(selectedRow);
          break;
        case "DOWNLOAD":
          await handleDownload(selectedRow);
          break;
        default:
          break;
      }

      if (currentSearchRequest) {
        await searchHandler(currentSearchRequest);
      }
      setSelectedRows([]);
    } catch (error) {
      console.error("Action menu error:", error);
      errorMessageToast(error);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handleApprove = async (row) => {
    const approvalId = row.approvalId;

    if (!approvalId) {
      toast.error("No approval workflow found for this request", {
        autoClose: 3000,
        containerId: "Main",
      });
      throw new Error("No approval workflow");
    }

    try {
      const payload = {
        workflowId: approvalId,
        decision: "APPROVED",
        comments: "Approved via Report Queue",
      };

      const response = await approveReport(payload);

      toast.success(response?.comments || "Report approved successfully", {
        autoClose: 3000,
        containerId: "Main",
      });
    } catch (error) {
      console.error("Approval error:", error);
      toast.error("Failed to approve report", {
        autoClose: 5000,
        containerId: "Main",
      });
      throw error;
    }
  };

  const handleCancel = async (row) => {
    const requestId = row.reportRequestId;

    try {
      await cancelReport(requestId);

      toast.success("Report request cancelled successfully", {
        autoClose: 3000,
        containerId: "Main",
      });
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error("Failed to cancel report", {
        autoClose: 5000,
        containerId: "Main",
      });
      throw error;
    }
  };

  const handleDownload = async (row) => {
    const requestId = row.reportRequestId;

    try {
      const downloadInfo = await getReportDownloadInfo(requestId);

      if (
        downloadInfo?.reportStatus !== "COMPLETED" ||
        !downloadInfo?.documentId
      ) {
        toast.warn("Report is not ready for download yet.", {
          autoClose: 4000,
          containerId: "Main",
        });
        return;
      }

      const fileResponse = await downloadFile(downloadInfo.documentId);

      if (fileResponse?.base64File) {
        const byteCharacters = atob(fileResponse.base64File);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type:
            fileResponse.contentType ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        const formattedDate = formatToDateOnlyFromJsonDate(row.requestDate);

        link.href = url;
        link.download =
          fileResponse.name || `${row.reportAlias}_${formattedDate}.xlsx`;

        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        toast.success("Download started", {
          autoClose: 3000,
          containerId: "Main",
        });
        return;
      }

      toast.error("File content not found", {
        autoClose: 3000,
        containerId: "Main",
      });
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download report", {
        autoClose: 5000,
        containerId: "Main",
      });
    }
  };

  const getActionMenuOptions = () => {
    if (selectedRows.length !== 1) return [];

    const selectedRow = selectedRows[0];
    const status = selectedRow.reportStatus;
    const hasApproved = selectedRow.hasApproved;

    if (status === "FAILED" || status === "CANCELLED") {
      return [];
    }

    const options = [];

    switch (status) {
      case "PENDING_APPROVAL":
        if (hasApproved) {
          options.push(
            { key: "APPROVE", text: "Approve", value: "APPROVE" },
            { key: "CANCEL", text: "Cancel", value: "CANCEL" },
          );
        } else {
          options.push({ key: "CANCEL", text: "Cancel", value: "CANCEL" });
        }
        break;

      case "QUEUED":
      case "PROCESSING":
        options.push({ key: "CANCEL", text: "Cancel", value: "CANCEL" });
        break;

      case "COMPLETED":
        options.push({ key: "DOWNLOAD", text: "Download", value: "DOWNLOAD" });
        break;

      default:
        break;
    }

    return options;
  };

  const handleReset = () => {
    setIsAutoSearchMode(false);
    hasAutoSearched.current = false;
    previousParamId.current = null;

    if (params?.id) {
      navigate("/mis/report-generator/queue", { replace: true });
    }

    setSelectedReport("");
    setFromDate("");
    setToDate("");
    setResults([]);
    setSearchClicked(false);
    setSelectedRows([]);
    setCurrentSearchRequest(null);
  };

  return (
    <div>
      
      {isAutoSearchMode && results.length > 0 && (
        <div className="ui info message" style={{ marginBottom: "1rem" }}>
          <i className="info circle icon"></i>
          Showing report from notification. You can perform a new search below.
        </div>
      )}

      <div className="three fields">
        <Dropdown
          required={false}
          label="Report Type"
          placeholder="Select Report Type"
          options={reportTypes}
          value={selectedReport}
          onChange={(e, { value }) => {
            setSelectedReport(value);
          }}
        />
        <Input
          type="date"
          label="From Date"
          name="fromDate"
          value={fromDate ?? ""}
          onChange={(e) => {
            setFromDate(e.target.value);
          }}
        />
        <Input
          label="To Date"
          type="date"
          name="toDate"
          value={toDate ?? ""}
          onChange={(e) => {
            setToDate(e.target.value);
          }}
        />
      </div>

      <div
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <div className="field" style={{ width: "100%" }}>
          <Button template="search" onClick={handleSearch} />
          <Button template="searchReset" onClick={handleReset} />
        </div>
      </div>

      {searchClicked && (
        <>
          {isSearchLoading ? (
            <LoadingSpinner />
          ) : (
            <Grid
              ref={gridRef}
              isLoading={isSearchLoading}
              onSelectionChanged={handleSelectionChanged}
              showActionMenu={
                selectedRows.length === 1 && getActionMenuOptions().length > 0
              }
              downloadOnlySelected={false}
              heading="Report Queue"
              actionMenuOptions={getActionMenuOptions()}
              multiRowSelection={false}
              handleActionMenuChange={actionMenuHandler}
              content={results}
              config={[
                {
                  headerName: "Report Request Code",
                  field: "reportRequestCode",
                  headerCheckboxSelection: true,
                  checkboxSelection: true,
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Report Name",
                  field: "reportAlias",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Report Request By",
                  field: "requestedBy",
                },
                {
                  headerName: "Report Request Date",
                  field: "requestDateFormatted",
                },
                {
                  headerName: "Status",
                  field: "reportStatus",
                  cellRenderer: ReportStatusCellRenderer,
                  cellStyle: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "12px",
                  },
                },
                {
                  headerName: "Started At",
                  field: "startedAtFormatted",
                },
                {
                  headerName: "Completed At",
                  field: "completedAtFormatted",
                },
                {
                  headerName: "Approved By",
                  field: "approvedBy",
                },
              ]}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ReportQueue;
